import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  CssBaseline,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Grid,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Formik } from "formik";
import * as yup from "yup";

import Header from "../../../../../components/header/Header";
import { tokens } from "../../../../../theme";
import SnackBar from "../../../../../components/snackbar/SnackBar";
import { getDriverAndVehicle } from "../../../../../api/users/driver/driver";
import {
  getTokenRefresh,
  verifyExpireTokenRefresh,
} from "../../../../../helpers/token/refreshToken";
import { getEnterpriseEmployeeAddress } from "../../../../../api/enterprise/address";
import { updateBookingBusinessCredit } from "../../../../../api/booking/bookingBusiness";
import { getBookingBusinessByID } from "../../../../../api/booking/bookingBusiness";
import Topbar from "../../../../../components/topbar/TopBar";
//sidebar
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import Sidebar from "../../../../../components/sidebar/Sidebar";
import { EndDateTimeDialog } from "../components/EndDateTimeDialog";
import {
  formatDatePickerValue,
  formatTimePickerValue,
  getDateTimeValue,
  getDateOnlyValue,
} from "../components/endDateTimeUtils";
import BookingBusiness from "./create/BookingBusiness";

function normalizeRoute(route) {
  if (!route) return null;

  return {
    id: route.id,
    name: route.name,
    lat: route.lat ?? route.latitude,
    lng: route.lng ?? route.longitude,
    latitude: route.latitude ?? route.lat,
    longitude: route.longitude ?? route.lng,
  };
}

function parseSubRoutes(subRoutes) {
  if (!subRoutes) return [];
  if (Array.isArray(subRoutes)) {
    return subRoutes.map(normalizeRoute).filter(Boolean);
  }

  if (typeof subRoutes !== "string") return [];
  const value = subRoutes.trim();
  if (!value || value === "[]") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.map(normalizeRoute).filter(Boolean)
      : [];
  } catch {
    const routes = [];
    const objectMatches = value.matchAll(/\{([^}]*)\}/g);

    for (const match of objectMatches) {
      const route = {};
      const keyValueMatches = match[1].matchAll(
        /(\w+)\s*=\s*(".*?"|'.*?'|[^,}]+)/g,
      );

      for (const keyValueMatch of keyValueMatches) {
        const key = keyValueMatch[1];
        const rawValue = keyValueMatch[2].trim();
        const cleanValue = rawValue.replace(/^["']|["']$/g, "");
        route[key] = key === "id" ? Number(cleanValue) : cleanValue;
      }

      const normalizedRoute = normalizeRoute(route);
      if (normalizedRoute?.name) routes.push(normalizedRoute);
    }

    return routes;
  }
}

function getRouteListFromBooking(booking) {
  const subRoutes = parseSubRoutes(booking?.sub_routes);
  const destination = normalizeRoute({
    name: booking?.destination_name,
    lat: booking?.destination_lat,
    lng: booking?.destination_lng,
  });

  return [...subRoutes, destination]
    .filter((route) => route?.name)
    .map((route, index) => ({
      ...route,
      id: route.id ?? index + 1,
      routeId: `booking-credit-route-${route.id ?? index}-${route.name}-${route.lat}-${route.lng}`,
    }));
}

function getNextRouteId(routeList) {
  const ids = routeList.map((route) => Number(route.id || 0));
  return Math.max(0, ...ids) + 1;
}

function getRoutePayload(routeList) {
  const routes = Array.isArray(routeList)
    ? routeList.map(normalizeRoute).filter((route) => route?.name)
    : [];
  const destination = routes[routes.length - 1];
  const subRoutes = routes.slice(0, -1).map((route, index) => ({
    id: index + 1,
    name: route.name,
    lat: route.lat,
    lng: route.lng,
  }));

  return {
    destination: destination
      ? {
          name: destination.name,
          lat: destination.lat,
          lng: destination.lng,
        }
      : null,
    subRoutes,
  };
}

function getDatePickerValue(value) {
  if (!value) return null;
  if (value?.$d) return value;
  if (value instanceof Date) return dayjs(value);

  if (typeof value === "string") {
    const [day, month, year] = value.split("/");
    if (!day || !month || !year) return null;

    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : dayjs(date);
  }

  return null;
}

function getTimePickerValue(value) {
  if (!value) return null;
  if (value?.$d) return value;
  if (value instanceof Date) return dayjs(value);

  if (typeof value === "string") {
    const [hourMinute, meridiem] = value.trim().split(" ");
    const [rawHour, rawMinute] = (hourMinute ?? "").split(":");
    let hour = Number(rawHour);
    const minute = Number(rawMinute);

    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    if (meridiem === "PM" && hour !== 12) hour += 12;
    if (meridiem === "AM" && hour === 12) hour = 0;

    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    return dayjs(date);
  }

  return null;
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems;
}

function RouteSortableItem({ route, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(route.routeId),
    index,
    group: "booking-credit-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {route.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar ruta"
        onClick={() => onDelete(route.routeId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const UpdateBookingCredit = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [driver, setDriver] = useState(null);

  const [commissionOption, setCommissionOption] = useState(true);
  const [commissionValue, setCommissionValue] = useState(0);
  const [otherPersonDialogOpen, setOtherPersonDialogOpen] = useState(false);
  const [otherPersonForm, setOtherPersonForm] = useState({
    other_name: "",
    other_phone: "",
  });
  const [initialBookingTypeId, setInitialBookingTypeId] = useState(null);
  const [endDateTimeDialogOpen, setEndDateTimeDialogOpen] = useState(false);
  const [dateEndFormat, setDateEndFormat] = useState("");
  const [timeEndFormat, setTimeEndFormat] = useState("");
  // lista de direcciones
  const [addressApi, setAddressApi] = useState([]);
  const [addressOrigin, setAddressOrigin] = useState(null);
  const [addressDestination, setAddressDestination] = useState(null);
  const [routesModalOpen, setRoutesModalOpen] = useState(false);
  const [routeModalList, setRouteModalList] = useState([]);
  const [routeModalDestinationToAdd, setRouteModalDestinationToAdd] =
    useState(null);
  const [routeModalDestinationInput, setRouteModalDestinationInput] =
    useState("");

  useEffect(() => {
    //buscar
    const searchBooking = async () => {
      try {
        console.log(id);
        const response = await getBookingBusinessByID("token", id);
        console.log(response.data);
        setAddressOrigin({
          name: response.data[0].origin_name,
          latitude: response.data[0].origin_lat,
          longitude: response.data[0].origin_lng,
        });
        setAddressDestination({
          name: response.data[0].destination_name,
          latitude: response.data[0].destination_lat,
          longitude: response.data[0].destination_lng,
        });
        setDriver({
          driver_id: response.data[0].drivers_id,
          name: response.data[0].driver_name,
          lastname: response.data[0].driver_lastname,
          vehicle_id: response.data[0].vehicles_id,
          brand: response.data[0].vehicle_brand,
          model: response.data[0].vehicle_model,
          bodywork: response.data[0].vehicle_bodywork,
          plate: response.data[0].vehicle_plate,
          color: response.data[0].vehicle_color,
          percentageValue: response.data[0].payment_commission,
          phone: response.data[0].phone,
        });
        const bookingData = response.data[0];
        const routes = getRouteListFromBooking(bookingData);
        bookingData.clientName = `${bookingData.client_name} ${bookingData.client_lastname}`;
        bookingData.subRoutesList = routes;
        setInitialBookingTypeId(
          bookingData.booking_type_id ?? bookingData.booking_type ?? "",
        );
        setDateEndFormat(bookingData.dateEnd ?? "");
        setTimeEndFormat(bookingData.timeEnd ?? "");
        setRouteModalList(routes);
        setBooking(bookingData);
      } catch (error) {
        console.log(error);
      }
    };
    searchBooking();
  }, []);

  useEffect(() => {
    const driversData = async () => {
      const verify = verifyExpireTokenRefresh();
      if (verify) return console.log("no token");

      const token = getTokenRefresh();
      try {
        const response = await getDriverAndVehicle(token);
        console.log(response?.data);
        setDrivers(response?.data);
      } catch (error) {
        console.log(error);
      }
    };
    driversData();
  }, []);

  useEffect(() => {
    if (booking) {
      const getAddressEnterprise = async () => {
        try {
          const response = await getEnterpriseEmployeeAddress(
            "",
            booking.client_id,
            booking.enterprise_id,
          );
          setAddressApi([]);
          Object.values(response.data).map((address) => {
            const _address = {
              id: address.id,
              latitude: address.latitude,
              longitude: address.longitude,
              name: address.name,
            };
            setAddressApi((oldData) => [...oldData, _address]);
          });
        } catch (error) {
          console.log(error);
        }
      };
      getAddressEnterprise();
    }
  }, [booking]);

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const handleMouseDownPassword = (e) => e.preventDefault();
  const handleCloseRoutesModal = () => setRoutesModalOpen(false);
  const handleCloseOtherPersonDialog = () => setOtherPersonDialogOpen(false);
  const handleOpenEndDateTimeDialog = (values) => {
    setDateEndFormat(values.dateEnd ?? "");
    setTimeEndFormat(values.timeEnd ?? "");
    setEndDateTimeDialogOpen(true);
  };
  const handleCloseEndDateTimeDialog = () => setEndDateTimeDialogOpen(false);

  const handleOpenOtherPersonDialog = (values) => {
    setOtherPersonForm({
      other_name: values.other_name ?? "",
      other_phone: values.other_phone ?? "",
    });
    setOtherPersonDialogOpen(true);
  };

  const handleOtherPersonChange = (event) => {
    const { name, value } = event.target;
    setOtherPersonForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleAcceptOtherPerson = (setFieldValue) => {
    setFieldValue("other_name", otherPersonForm.other_name);
    setFieldValue("other_phone", otherPersonForm.other_phone);
    setOtherPersonDialogOpen(false);
  };

  const handleClearOtherPerson = (setFieldValue) => {
    setOtherPersonForm({
      other_name: "",
      other_phone: "",
    });
    setFieldValue("other_name", "");
    setFieldValue("other_phone", "");
  };

  const hasOtherPersonData = (values) => {
    return Boolean(
      String(values.other_name ?? "").trim() ||
      String(values.other_phone ?? "").trim(),
    );
  };

  const handleOpenRoutesModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setRouteModalList((current) => {
      if (current.length > 0) return current;
      return addressDestination
        ? [
            {
              ...normalizeRoute(addressDestination),
              id: 1,
              routeId: `booking-credit-route-destination-${addressDestination.name}-${addressDestination.latitude}-${addressDestination.longitude}`,
            },
          ]
        : [];
    });
    setRouteModalDestinationToAdd(null);
    setRouteModalDestinationInput("");
    setRoutesModalOpen(true);
  };

  const handleDestinationChange = (newValue) => {
    setAddressDestination(newValue);
    const destination = normalizeRoute(newValue);
    if (!destination?.name) {
      setRouteModalList([]);
      return;
    }

    setRouteModalList((current) => {
      const nextRoute = {
        ...destination,
        id: current.length > 0 ? current[current.length - 1].id : 1,
        routeId: `booking-credit-route-destination-${destination.name}-${destination.lat}-${destination.lng}`,
      };

      if (current.length === 0) return [nextRoute];
      return [...current.slice(0, -1), nextRoute];
    });
  };

  const handleAddRouteDestination = (newValue) => {
    if (!newValue) {
      setRouteModalDestinationToAdd(null);
      return;
    }

    const destination = normalizeRoute(newValue);
    if (!destination?.name) return;

    setRouteModalList((current) => {
      const id = getNextRouteId(current);
      const nextRoute = {
        ...destination,
        id,
        routeId: `booking-credit-route-${id}-${destination.name}-${destination.lat}-${destination.lng}`,
      };

      return [...current, nextRoute];
    });
    setRouteModalDestinationToAdd(null);
    setRouteModalDestinationInput("");
  };

  const handleDeleteRouteDestination = (routeId) => {
    setRouteModalList((current) =>
      current.filter((route) => route.routeId !== routeId),
    );
  };

  const handleRouteDragEnd = (event) => {
    const operation = event.operation;
    if (!operation || operation.canceled) return;

    setRouteModalList((current) => {
      let sourceIndex = -1;
      let targetIndex = -1;

      if (isSortableOperation(operation)) {
        sourceIndex = operation.source.initialIndex;
        targetIndex = operation.source.index;
      } else {
        const sourceId = String(operation.source?.id ?? "");
        const targetId = String(operation.target?.id ?? "");
        sourceIndex = current.findIndex((route) => route.routeId === sourceId);
        targetIndex = current.findIndex((route) => route.routeId === targetId);
      }

      if (
        sourceIndex < 0 ||
        targetIndex < 0 ||
        sourceIndex >= current.length ||
        targetIndex >= current.length ||
        sourceIndex === targetIndex
      ) {
        return current;
      }

      return arrayMove(current, sourceIndex, targetIndex);
    });
  };

  const handleAcceptRoutesModal = (setFieldValue) => {
    const routeValues = getRoutePayload(routeModalList);
    if (!routeValues.destination) {
      snackBarDialog("Debe seleccionar un destino", "error");
      return;
    }

    const destination = {
      name: routeValues.destination.name,
      latitude: routeValues.destination.lat,
      longitude: routeValues.destination.lng,
    };

    setAddressDestination(destination);
    setFieldValue("destination", routeValues.destination);
    setFieldValue("sub_routes", routeValues.subRoutes);
    setFieldValue("subRoutesList", routeModalList);
    handleCloseRoutesModal();
  };

  const handleAcceptEndDateTimeDialog = (values, setFieldValue) => {
    const startDate = getDateOnlyValue(values.date);
    const endDate = getDateOnlyValue(dateEndFormat);
    const startDateTime = getDateTimeValue(values.date, values.time);
    const endDateTime = getDateTimeValue(dateEndFormat, timeEndFormat);

    if (!startDate || !endDate) {
      snackBarDialog("Fecha final invalida", "error");
      return;
    }

    if (endDate < startDate) {
      snackBarDialog(
        "La fecha final debe ser igual o superior a la fecha inicial",
        "error",
      );
      return;
    }

    if (!startDateTime || !endDateTime) {
      snackBarDialog("Hora final invalida", "error");
      return;
    }

    if (endDateTime <= startDateTime) {
      snackBarDialog(
        "La hora final debe ser superior a la hora inicial",
        "error",
      );
      return;
    }

    const formattedDateEnd = formatDatePickerValue(dateEndFormat);
    const formattedTimeEnd = formatTimePickerValue(timeEndFormat);

    setDateEndFormat(formattedDateEnd);
    setTimeEndFormat(formattedTimeEnd);
    setFieldValue("booking_type_id", 3);
    setFieldValue("dateEnd", formattedDateEnd);
    setFieldValue("timeEnd", formattedTimeEnd);
    setEndDateTimeDialogOpen(false);
  };

  const handleClearEndDateTimeDialog = (setFieldValue) => {
    setDateEndFormat("");
    setTimeEndFormat("");
    setFieldValue("booking_type_id", initialBookingTypeId);
    setFieldValue("dateEnd", "");
    setFieldValue("timeEnd", "");
    setEndDateTimeDialogOpen(false);
  };

  const checkoutSchema = yup.object().shape({});

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    try {
      const routeValues = getRoutePayload(routeModalList);
      if (!routeValues.destination) {
        snackBarDialog("Debe seleccionar un destino", "error");
        setIsLoading(false);
        return;
      }

      const origin = {
        name: addressOrigin.name,
        lat: addressOrigin.latitude,
        lng: addressOrigin.longitude,
      };
      const destination = {
        name: routeValues.destination.name,
        lat: routeValues.destination.lat,
        lng: routeValues.destination.lng,
      };
      values.driver = driver;
      values.origin = origin;
      values.destination = destination;
      values.sub_routes = routeValues.subRoutes;
      const updateCommission = !commissionOption
        ? values.payment_commission
        : driver.percentageValue;
      const formattedDate = formatDatePickerValue(values.date);
      const formattedTime = formatTimePickerValue(values.time);
      const formattedDateEnd = formatDatePickerValue(values.dateEnd);
      const formattedTimeEnd = formatTimePickerValue(values.timeEnd);

      const bookingBusiness = {
        driver,
        origin: values.origin,
        destination: values.destination,
        date: formattedDate,
        time: formattedTime,
        persons: values.persons,
        notes: values.notes,
        booking_type: values.payment_type,
        currency: values.payment_currency,
        tax: values.payment_tax,
        toll: values.payment_toll,
        price: values.payment_price_driver,
        total: values.payment_price_total,
        discount: 0,
        commission: updateCommission,
        counterBooking: values.order_number,
        otherName: values.other_name ?? "",
        otherPhone: values.other_phone ?? "",
        bookingTypeId: values.booking_type_id ?? initialBookingTypeId,
        booking_type_id: values.booking_type_id ?? initialBookingTypeId,
        dateEnd: formattedDateEnd,
        timeEnd: formattedTimeEnd,
        subRoutes: routeValues.subRoutes,
        paymentTypeId: values.payment_type,
      };

      console.log(bookingBusiness);

      await updateBookingBusinessCredit("token", bookingBusiness);
      snackBarDialog("RESERVA ACTUALIZADA");
      navigate("/admin/history/booking-credit");
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      snackBarDialog("ERROR: NO SE PUDO ACTUALIZAR ESTA RESERVA", "error");
    }
  };

  return (
    <div>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="5px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Header title="PEDIDO CREDITO: Editar" subtitle="" />
            </Box>
            <Box display="grid" gap="10px">
              <Box gridColumn="span 12" gridRow="span 1">
                {booking && (
                  <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={booking}
                    validationSchema={checkoutSchema}
                  >
                    {({
                      values,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      setFieldValue,
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Box backgroundColor={colors.primary[400]} p={1}>
                          <Grid container spacing={1} p={1}>
                            <Grid item xs={12} sm={6}>
                              {addressApi ? (
                                <Autocomplete
                                  value={addressOrigin}
                                  disablePortal
                                  name="client"
                                  id="combo-box-origin"
                                  onChange={(e, newValue) => {
                                    setAddressOrigin(newValue);
                                  }}
                                  options={addressApi}
                                  getOptionLabel={(option) => `${option.name}`}
                                  renderInput={(params) => (
                                    <TextField {...params} label="Origen" />
                                  )}
                                />
                              ) : (
                                <TextField
                                  fullWidth
                                  disabled
                                  type="text"
                                  label={t("Lista de Direcciones")}
                                />
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {addressApi ? (
                                <Autocomplete
                                  value={addressDestination}
                                  disablePortal
                                  name="client"
                                  id="combo-box-origin"
                                  onChange={(e, newValue) => {
                                    handleDestinationChange(newValue);
                                  }}
                                  options={addressApi}
                                  getOptionLabel={(option) => `${option.name}`}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Destino"
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              edge="end"
                                              aria-label="rutas de destino"
                                              onMouseDown={(event) => {
                                                event.preventDefault();
                                              }}
                                              onClick={handleOpenRoutesModal}
                                            >
                                              <LocationOnIcon />
                                            </IconButton>
                                            {params.InputProps.endAdornment}
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                />
                              ) : (
                                <TextField
                                  fullWidth
                                  disabled
                                  type="text"
                                  label={t("Lista de Direcciones")}
                                />
                              )}
                            </Grid>
                            <Grid item container xs={12} md={6} spacing={0.5}>
                              <Grid
                                item
                                xs={12}
                                sm={hasOtherPersonData(values) ? 6 : 12}
                              >
                                <TextField
                                  value={values.clientName}
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="text"
                                  label={t("client")}
                                  name="client"
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="datos de otra persona"
                                          edge="end"
                                          onClick={() =>
                                            handleOpenOtherPersonDialog(values)
                                          }
                                        >
                                          <PersonIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              {hasOtherPersonData(values) && (
                                <>
                                  <Grid item xs={6} sm={3}>
                                    <TextField
                                      value={values.other_name ?? ""}
                                      fullWidth
                                      variant="outlined"
                                      color="success"
                                      type="text"
                                      label="PARA:"
                                      name="other_name"
                                      InputProps={{ readOnly: true }}
                                      inputProps={{
                                        style: { textTransform: "uppercase" },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <TextField
                                      value={values.other_phone ?? ""}
                                      fullWidth
                                      variant="outlined"
                                      color="success"
                                      type="tel"
                                      label="Tel:"
                                      name="other_phone"
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Grid>
                                </>
                              )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={3} md={3}>
                                  <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                  >
                                    <MobileDatePicker
                                      label={t("date")}
                                      inputFormat="DD/MM/YYYY"
                                      name="date"
                                      value={getDatePickerValue(values.date)}
                                      onChange={(newValue) => {
                                        setFieldValue("date", newValue);
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          fullWidth
                                          variant="outlined"
                                          color="success"
                                          onBlur={handleBlur}
                                        />
                                      )}
                                    />
                                  </LocalizationProvider>
                                </Grid>
                                <Grid item xs={6} sm={3} md={3}>
                                  <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                  >
                                    <MobileTimePicker
                                      label={t("time")}
                                      name="time"
                                      value={getTimePickerValue(values.time)}
                                      onChange={(newValue) => {
                                        setFieldValue("time", newValue);
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          fullWidth
                                          variant="outlined"
                                          color="success"
                                          onBlur={handleBlur}
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <>
                                                <InputAdornment position="end">
                                                  <IconButton
                                                    edge="end"
                                                    aria-label="fecha y hora final"
                                                    onMouseDown={(event) => {
                                                      event.preventDefault();
                                                      event.stopPropagation();
                                                    }}
                                                    onClick={(event) => {
                                                      event.preventDefault();
                                                      event.stopPropagation();
                                                      handleOpenEndDateTimeDialog(
                                                        values,
                                                      );
                                                    }}
                                                  >
                                                    <AccessTimeIcon />
                                                  </IconButton>
                                                </InputAdornment>
                                                {
                                                  params.InputProps
                                                    ?.endAdornment
                                                }
                                              </>
                                            ),
                                          }}
                                        />
                                      )}
                                    />
                                  </LocalizationProvider>
                                </Grid>
                                <Grid item xs={6} sm={3} md={3}>
                                  <TextField
                                    value={values.persons}
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    type="text"
                                    label={t("persons")}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    name="persons"
                                  />
                                </Grid>
                                <Grid item xs={6} sm={3} md={3}>
                                  <TextField
                                    value={values.vehicle_bodywork}
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    type="text"
                                    label={t("Modelo")}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    name="vehicle_bodywork"
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                value={values.notes}
                                name="notes"
                                fullWidth
                                variant="outlined"
                                color="success"
                                type="text"
                                label={t("notes")}
                                onBlur={handleBlur}
                                onChange={handleChange}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {drivers && (
                                <Autocomplete
                                  value={driver}
                                  disablePortal
                                  id="combo-box-driver"
                                  onChange={(e, newValue) => {
                                    setDriver(newValue);
                                  }}
                                  options={drivers}
                                  getOptionLabel={(option) =>
                                    `${option.name} ${option.lastname} ${option.brand} ${option.model} ${option.bodywork} ${option.plate} ${option.color}`
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t("driver")}
                                    />
                                  )}
                                />
                              )}
                            </Grid>

                            <Grid item xs={12} sm={3} md={3}>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={12} md={12}>
                                  <FormControl fullWidth>
                                    <InputLabel>{t("Tipo Pago")}</InputLabel>
                                    <Select
                                      value={values.payment_type}
                                      id="payment_type"
                                      name="payment_type"
                                      label={t("payment")}
                                      onChange={handleChange}
                                    >
                                      <MenuItem value={1}>
                                        {t("EFECTIVO")}
                                      </MenuItem>
                                      <MenuItem value={2}>
                                        {t("CREDITO")}
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={12} md={12}>
                                  <FormControl fullWidth variant="outlined">
                                    <InputLabel>COMISION</InputLabel>
                                    <OutlinedInput
                                      value={values.payment_commission}
                                      name="payment_commission"
                                      onChange={handleChange}
                                      disabled={commissionOption}
                                      endAdornment={
                                        <InputAdornment position="end">
                                          <IconButton
                                            aria-label="toggle password visibility"
                                            onMouseDown={
                                              handleMouseDownPassword
                                            }
                                            edge="end"
                                            onClick={(e) => {
                                              setCommissionOption(
                                                !commissionOption,
                                              );
                                            }}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      }
                                      label="COMISION"
                                      aria-describedby="outlined-helper-text"
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12} sm={3} md={3}>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={12} md={12}>
                                  <FormControl fullWidth>
                                    <InputLabel>{t("currency")}</InputLabel>
                                    <Select
                                      value={values.payment_currency}
                                      id="payment_currency"
                                      name="payment_currency"
                                      label={t("currency")}
                                      onChange={handleChange}
                                    >
                                      <MenuItem value={1}>{t("PEN")}</MenuItem>
                                      <MenuItem value={2}>{t("USD")}</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                {(values.dateEnd || values.timeEnd) && (
                                  <Grid item xs={6} sm={12} md={12}>
                                    <Grid container spacing={1}>
                                      {values.dateEnd && (
                                        <Grid item xs={6} sm={6} md={6}>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                          >
                                            <MobileDatePicker
                                              label={t("dateEnd")}
                                              inputFormat="DD/MM/YYYY"
                                              name="dateEnd"
                                              value={getDatePickerValue(
                                                values.dateEnd,
                                              )}
                                              onChange={(newValue) => {
                                                setFieldValue(
                                                  "dateEnd",
                                                  newValue,
                                                );
                                                setDateEndFormat(newValue);
                                              }}
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  fullWidth
                                                  variant="outlined"
                                                  color="success"
                                                  onBlur={handleBlur}
                                                />
                                              )}
                                            />
                                          </LocalizationProvider>
                                        </Grid>
                                      )}
                                      {values.timeEnd && (
                                        <Grid item xs={6} sm={6} md={6}>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                          >
                                            <MobileTimePicker
                                              label={t("timeEnd")}
                                              name="timeEnd"
                                              value={getTimePickerValue(
                                                values.timeEnd,
                                              )}
                                              onChange={(newValue) => {
                                                setFieldValue(
                                                  "timeEnd",
                                                  newValue,
                                                );
                                                setTimeEndFormat(newValue);
                                              }}
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  fullWidth
                                                  variant="outlined"
                                                  color="success"
                                                  onBlur={handleBlur}
                                                />
                                              )}
                                            />
                                          </LocalizationProvider>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>

                            <Grid item xs={12} sm={3} md={3}>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={12} md={12}>
                                  <TextField
                                    value={values.payment_tax}
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    type="number"
                                    label={t("IMPUESTO")}
                                    onChange={handleChange}
                                    name="payment_tax"
                                  />
                                </Grid>
                                <Grid item xs={6} sm={12} md={12}>
                                  <TextField
                                    value={values.payment_toll}
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    type="number"
                                    label={t("PEAJE")}
                                    onChange={handleChange}
                                    name="payment_toll"
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12} sm={3} md={3}>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={12} md={12}>
                                  <TextField
                                    value={values.payment_price_driver}
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    type="number"
                                    label={t("P CONDUCTOR")}
                                    onChange={handleChange}
                                    name="payment_price_driver"
                                  />
                                </Grid>
                                <Grid item xs={6} sm={12} md={12}>
                                  <TextField
                                    value={values.payment_price_total}
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    type="number"
                                    label={t("TOTAL")}
                                    onChange={handleChange}
                                    name="payment_price_total"
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={0} sm={9}></Grid>
                            <Grid item xs={12} sm={3} md={3}>
                              <Button
                                fullWidth
                                type="submit"
                                color="secondary"
                                variant="contained"
                                sx={{ height: 45 }}
                              >
                                {"ACTUALIZAR"}
                              </Button>
                            </Grid>
                          </Grid>
                          <SnackBar
                            stateSnk={stateSnk}
                            vertical={vertical}
                            horizontal={horizontal}
                            duration={4000}
                            handleClose={handleSnkClose}
                            severity={severity}
                            messageSnk={messageSnk}
                          />
                          <Dialog
                            open={routesModalOpen}
                            onClose={handleCloseRoutesModal}
                            fullWidth
                            maxWidth="xs"
                            PaperProps={{
                              sx: {
                                height: "80vh",
                                maxHeight: "90vh",
                              },
                            }}
                          >
                            <DialogTitle>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                sx={{
                                  alignItems: {
                                    xs: "stretch",
                                    sm: "center",
                                  },
                                }}
                              >
                                <Typography variant="h6" sx={{ flexShrink: 0 }}>
                                  Rutas
                                </Typography>
                                <Autocomplete
                                  value={routeModalDestinationToAdd}
                                  inputValue={routeModalDestinationInput}
                                  onChange={(event, newValue) => {
                                    handleAddRouteDestination(newValue);
                                  }}
                                  onInputChange={(
                                    event,
                                    newInputValue,
                                    reason,
                                  ) => {
                                    if (reason === "reset") return;
                                    setRouteModalDestinationInput(
                                      newInputValue,
                                    );
                                  }}
                                  options={addressApi || []}
                                  getOptionLabel={(option) =>
                                    `${option?.name || ""}`.trim()
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    option?.id === value?.id ||
                                    (option?.name === value?.name &&
                                      option?.latitude === value?.latitude &&
                                      option?.longitude === value?.longitude)
                                  }
                                  sx={{ flex: 1 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t("Destino")}
                                      size="small"
                                    />
                                  )}
                                />
                              </Stack>
                            </DialogTitle>
                            <DialogContent>
                              <Stack spacing={1} sx={{ pt: 1 }}>
                                <DragDropProvider
                                  onDragEnd={handleRouteDragEnd}
                                >
                                  <Stack spacing={1}>
                                    {routeModalList.map((route, index) => (
                                      <RouteSortableItem
                                        key={route.routeId}
                                        route={route}
                                        index={index}
                                        onDelete={handleDeleteRouteDestination}
                                      />
                                    ))}
                                  </Stack>
                                </DragDropProvider>
                              </Stack>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={handleCloseRoutesModal}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="secondary"
                                onClick={() =>
                                  handleAcceptRoutesModal(setFieldValue)
                                }
                              >
                                Actualizar
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <Dialog
                            open={otherPersonDialogOpen}
                            onClose={handleCloseOtherPersonDialog}
                            fullWidth
                            maxWidth="sm"
                          >
                            <DialogTitle>Datos de otra persona</DialogTitle>
                            <DialogContent>
                              <Stack spacing={2} sx={{ pt: 1 }}>
                                <TextField
                                  value={otherPersonForm.other_name}
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="text"
                                  label="Nombre"
                                  name="other_name"
                                  onChange={handleOtherPersonChange}
                                  inputProps={{
                                    style: { textTransform: "uppercase" },
                                  }}
                                />
                                <TextField
                                  value={otherPersonForm.other_phone}
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="tel"
                                  label="Telefono"
                                  name="other_phone"
                                  onChange={handleOtherPersonChange}
                                />
                              </Stack>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={handleCloseOtherPersonDialog}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="warning"
                                onClick={() =>
                                  handleClearOtherPerson(setFieldValue)
                                }
                              >
                                Limpiar
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="secondary"
                                onClick={() =>
                                  handleAcceptOtherPerson(setFieldValue)
                                }
                              >
                                Aceptar
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <EndDateTimeDialog
                            open={endDateTimeDialogOpen}
                            onClose={handleCloseEndDateTimeDialog}
                            dateEnd={dateEndFormat}
                            timeEnd={timeEndFormat}
                            dateLabel={t("dateEnd")}
                            timeLabel={t("timeEnd")}
                            onDateEndChange={(newValue) => {
                              setDateEndFormat(newValue);
                            }}
                            onTimeEndChange={(newValue) => {
                              setTimeEndFormat(newValue);
                            }}
                            onClear={() =>
                              handleClearEndDateTimeDialog(setFieldValue)
                            }
                            onAccept={() =>
                              handleAcceptEndDateTimeDialog(
                                values,
                                setFieldValue,
                              )
                            }
                          />
                        </Box>
                      </form>
                    )}
                  </Formik>
                )}
              </Box>
            </Box>
          </Box>
        </main>
      </div>
    </div>
  );
};

export default UpdateBookingCredit;
